// @ts-strict-ignore
import { sumArray, unique } from '../../../../functions/utilities/array-utility';
import { TransitionElement } from '../../../../model/enums/transition-element';
import {
  Cost,
  CostType,
  ICarbonCausalChain,
  Input,
  OperationGrowthType,
  Output,
  Work,
  WORK_TO_SKIP,
} from '../../model/carbon-causal-chain';
import { EconomicStockImpactType } from '../../model/climate-cortex-model';
import { EvaluatedFactor } from '../../model/factor';
import { IGlobalExternality } from '../../model/global-externality';
import { ResourceType } from '../../model/resource';
import { getValueFromFactor, getValueFromVariableName, instantiateFactor } from '../../utils/factor-util';
import { KeyMap } from '../key-map';
import { Resource } from '../resource/resource';
import { Stock } from '../stock/stock';
import { CostResults } from '../transition-data';
import { Variable } from '../variable/variable';

export class CarbonCausalChain {
  id: string;
  work: Work[];
  private _carbonAbatementPerTE: KeyMap<number> = {};
  private _costs: Cost[];
  private _growthFactor: EvaluatedFactor;
  private _initialOperationRef: string;
  private _initialOperations: number;
  private _initialPopulation: number;
  private _initialWorkRelatedVariableValues: KeyMap<number> = {};
  private _isSupplySide: boolean;
  private _operationGrowthFactorPerCapita: number;
  private _operationGrowthType: OperationGrowthType;
  private _operations: number;
  /* Operations shifted are accumulative over time, which means that if <5, 100>, <6, 120> then 20 operations has been shifted year 6 */
  private _operationsShifted: Map<number, number> = new Map<number, number>();
  private _operationsShiftedByTe: Map<TransitionElement, Map<number, number>> = new Map<
    TransitionElement,
    Map<number, number>
  >();
  private _populationGrowthRate: number;
  private _resources: KeyMap<Resource>;

  get carbonAbatementPerTE(): KeyMap<number> {
    return this._carbonAbatementPerTE;
  }

  get initialOperations(): number {
    return this._initialOperations;
  }

  get operations(): number {
    return this._operations;
  }

  get producedResources(): string[] {
    const resourcesProduced = this.work
      .map((work) => work.output?.map((output) => output.resource))
      .flat()
      .filter(unique);
    return resourcesProduced;
  }

  constructor(
    value: ICarbonCausalChain,
    variables: KeyMap<Variable>,
    resources: KeyMap<Resource>,
    isSupplySide: boolean
  ) {
    this.id = value.ccc;
    this._operationGrowthType = value.operation.growthType;
    this._initialOperationRef = value.operation.variable;
    if (value.operation.growthType && value.operation.growthType !== OperationGrowthType.No) {
      this._growthFactor = instantiateFactor(value.operation.growthFactor);
      this._operationGrowthFactorPerCapita = getValueFromFactor(this._growthFactor, variables);
    }
    this.work = this._instantiateWork(value.work?.filter((work) => !WORK_TO_SKIP.includes(work.work)) || []);
    this._costs = this._instantiateCosts(value.costs || []);
    this._resources = resources;
    this._isSupplySide = isSupplySide;
    this._init(variables);
  }

  /**
   * @returns what is successfully altered
   */
  alterOperation(diff: number, tReport: number, transitionElement: TransitionElement): number {
    if (isNaN(diff)) {
      return 0;
    }
    this._operations += diff;
    let altered = diff;
    if (this._operations < 0) {
      altered -= this._operations;
      this._operations = 0;
    }
    // set operations shifted by specific TE
    if (this._operationsShiftedByTe.get(transitionElement) === undefined) {
      this._operationsShiftedByTe.set(transitionElement, new Map<number, number>());
      this._operationsShiftedByTe.get(transitionElement).set(tReport, 0);
    } else if (this._operationsShiftedByTe.get(transitionElement).get(tReport) === undefined) {
      this._operationsShiftedByTe.get(transitionElement).set(tReport, 0);
    }
    this._operationsShiftedByTe
      .get(transitionElement)
      .set(tReport, this._operationsShiftedByTe.get(transitionElement).get(tReport) + altered);
    // set total operations shifted
    if (this._operationsShifted.get(tReport) === undefined) {
      this._operationsShifted.set(tReport, 0);
    }
    this._operationsShifted.set(tReport, this._operationsShifted.get(tReport) + altered);

    return altered;
  }

  calculateCosts(
    tSetup: number,
    tReport: number,
    variables: KeyMap<Variable>,
    resources: KeyMap<Resource>,
    stocks: KeyMap<Stock>,
    globalExternalities: KeyMap<IGlobalExternality>,
    cohort: number,
    discountRate: number,
    transitionElement?: TransitionElement,
    economicStockImpact?: EconomicStockImpactType
  ): CostResults[] {
    if (this._costs?.length) {
      const costs = [];
      const alteredVariables: string[] = [];
      // TODO, simplify this function to have one function of altering the variable values
      this._costs.forEach((evaluatedCost) => {
        if (evaluatedCost.resources) {
          evaluatedCost.resources.forEach((resource) => {
            if (resource.relativePriceChange) {
              alteredVariables.push(resource.pricePerUnit);
              const factor = Math.pow(1 + variables[resource.relativePriceChange].value / 100, tReport);
              const originalValue = variables[resource.pricePerUnit].value;
              variables[resource.pricePerUnit].alterValue(factor * originalValue - originalValue);
            }
          });
        }
        if (evaluatedCost.stocks) {
          evaluatedCost.stocks.forEach((stock) => {
            if (stock.relativePriceChange) {
              alteredVariables.push(stock.pricePerUnit);
              const factor = Math.pow(1 + variables[stock.relativePriceChange].value / 100, tReport);
              const originalValue = variables[stock.pricePerUnit].value;
              variables[stock.pricePerUnit].alterValue(factor * originalValue - originalValue);
            }
          });
        }
        if (evaluatedCost.externalities) {
          evaluatedCost.externalities.forEach((externality) => {
            if (externality.relativePriceChange) {
              alteredVariables.push(externality.pricePerFunctionUnit);
              const factor = Math.pow(1 + variables[externality.relativePriceChange].value / 100, tReport);
              const originalValue = variables[externality.pricePerFunctionUnit].value;
              variables[externality.pricePerFunctionUnit].alterValue(factor * originalValue - originalValue);
            }
          });
        }
        if (evaluatedCost.globalExternalities) {
          evaluatedCost.globalExternalities.forEach((globExt) => {
            const ext = globalExternalities[globExt.externality];
            if (ext.relativePriceChange) {
              alteredVariables.push(ext.pricePerFunctionUnit);
              const factor = Math.pow(1 + variables[ext.relativePriceChange].value / 100, tReport);
              const originalValue = variables[ext.pricePerFunctionUnit].value;
              variables[ext.pricePerFunctionUnit].alterValue(factor * originalValue - originalValue);
            }
          });
        }

        // filter out costs if they are not relevant
        if (!(economicStockImpact === EconomicStockImpactType.None && evaluatedCost.type === CostType.Capex)) {
          costs.push({
            name: evaluatedCost.name,
            ...this._evaluateCost(
              tSetup,
              tReport,
              evaluatedCost,
              variables,
              resources,
              stocks,
              cohort,
              discountRate,
              transitionElement,
              economicStockImpact
            ),
          });
        }
        alteredVariables.forEach((p) => variables[p].resetParameter());
      });
      return costs;
    }
  }

  calculateEmissionsFromOperations(
    operations: number,
    variables: KeyMap<Variable>,
    utilisationBau: boolean = false
  ): number {
    let totalCccEmissions = 0;
    this.work.forEach((work) => {
      const operationsToWorkCoefficient = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables, {
        bau: utilisationBau,
      });
      const workAmount = operations * operationsToWorkCoefficient;
      const combinedCccEmissionFactor = this.getWorkEmissionFactor(work, variables, utilisationBau);
      totalCccEmissions += workAmount * combinedCccEmissionFactor;
    });
    return totalCccEmissions;
  }

  calculateEmissionsPerResourceFromOperations(
    operations: number,
    variables: KeyMap<Variable>,
    bau: boolean = false
  ): { [key: string]: number } {
    const resourceSplitEmissions = {};
    this.work.forEach((work) => {
      const operationsToWorkCoefficient = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables, { bau });
      const workAmount = operations * operationsToWorkCoefficient;
      const emissionFactors = this._getProportionateResourceEmissionFactors(work, variables, bau);
      work.input.forEach((input, index) => {
        let resourceEmissions = resourceSplitEmissions[input.resource] || 0;
        resourceEmissions += workAmount * emissionFactors[index];
        resourceSplitEmissions[input.resource] = resourceEmissions;
      });
    });
    return resourceSplitEmissions;
  }

  // Assuming there is only 1 work item.
  calculateSimpleEnergyIntensityForCcc(variables: KeyMap<Variable>, utilisationBau: boolean = false): number {
    return getValueFromFactor(<EvaluatedFactor>this.work[0].operationToWork, variables, { bau: utilisationBau });
  }

  calculateSupply(t: number, variables: KeyMap<Variable>): void {
    this.work.forEach((work) => {
      work.output?.forEach((output) => {
        work.input.forEach((input) => {
          this._produceResource(t, variables, work, input, output);
        });
      });
    });
  }

  calculateWorkFromOperations(operations: number, variables: KeyMap<Variable>): number {
    // this implementation assumes all work items have the same unit of work
    let totalWork = 0;
    this.work.forEach((work) => {
      const operationsToWorkCoefficient = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables, {
        bau: true, // make this dynamic if needed
      });
      const workAmount = operations * operationsToWorkCoefficient;
      totalWork += workAmount;
    });
    return totalWork;
  }

  getEmissionFactorOfInput(
    input: Input,
    variables: KeyMap<Variable>,
    supplyBau: boolean,
    utilisationBau: boolean = false
  ): number {
    let emissionFactor = 0;
    if (this._isSupplySide) {
      emissionFactor = getValueFromFactor(<EvaluatedFactor>input.emissionFactor, variables);
    } else {
      if (this._resources[input.resource].type === ResourceType.GridSuppliedEnergy) {
        emissionFactor = supplyBau
          ? this._resources[input.resource].emissionFactorBau
          : this._resources[input.resource].emissionFactor;
      } else {
        emissionFactor = input.emissionFactor
          ? getValueFromFactor(<EvaluatedFactor>input.emissionFactor, variables, { bau: utilisationBau })
          : 1;
      }
    }
    return emissionFactor;
  }

  getEmissionsBau(t: number, variables: KeyMap<Variable>, supplyBau: boolean = false): number {
    const operations = this.getOperationsBau(t);
    let totalCccEmissions = 0;
    this.work.forEach((work) => {
      const operationsToWorkCoefficient = this._initialWorkRelatedVariableValues['energy_intensity'];
      const workAmount = operations * operationsToWorkCoefficient;
      work.input.forEach((input) => {
        const resourceProportion =
          typeof input.resourceProportion === 'string'
            ? this._initialWorkRelatedVariableValues[input.resourceProportion] / 100
            : 1;
        const workToResourceConversionCoefficient = getValueFromFactor(
          <EvaluatedFactor>input.resourceToWork,
          variables
        );
        const emissionFactor = this.getEmissionFactorOfInput(input, variables, supplyBau);
        totalCccEmissions += workAmount * resourceProportion * workToResourceConversionCoefficient * emissionFactor;
      });
    });
    return totalCccEmissions;
  }

  getEnergyEfficiencyFromResource(resource: string, variables: KeyMap<Variable>): number {
    const factor = this.work[0].output.find((output) => output.resource === resource)?.workToResource;
    return factor ? getValueFromFactor(<EvaluatedFactor>factor, variables) : 1;
  }

  getOperationsBau(t: number): number {
    let operations = this._initialOperations;
    if (this._operationGrowthType === OperationGrowthType.Only) {
      const popGrowth = this._populationAtYear(t + 1) - this._populationAtYear(t);
      operations = popGrowth * this._operationGrowthFactorPerCapita;
    } else if (this._operationGrowthType === OperationGrowthType.Yes) {
      const popGrowth = this._populationAtYear(t) - this._initialPopulation;
      operations = this._initialOperations + popGrowth * this._operationGrowthFactorPerCapita;
    }
    return operations;
  }

  /**
   * get Primary Output to Operations Conversion
   * only works with carbon causal chains having one work item with one primary output
   */
  getPOtOC(variables: KeyMap<Variable>): number {
    const conversionFactors = this.work.map((work) => {
      const energyIntensity = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables);

      const factor =
        work.output.length > 1
          ? work.output.find((output) => output.primary)?.workToResource
          : work.output[0]?.workToResource;
      const energyEfficiency = factor ? getValueFromFactor(<EvaluatedFactor>factor, variables) : 1;

      return 1 / energyEfficiency / energyIntensity;
    });
    return !isNaN(conversionFactors[0]) ? conversionFactors[0] : 1;
  }

  getResourceConsumption(
    tSetup: number,
    tReport: number,
    variables: KeyMap<Variable>,
    utilisationBau: boolean = false,
    cohort: number = 1,
    transitionElement?: TransitionElement
  ) {
    const resourceConsumptions: { [resource: string]: number } = {};
    this.work.forEach((work) => {
      const operationsToWorkCoefficient: number = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables, {
        bau: utilisationBau,
      });
      work.input.forEach((input) => {
        const proportion =
          typeof input.resourceProportion === 'string'
            ? (utilisationBau
                ? variables[input.resourceProportion].referenceValue
                : variables[input.resourceProportion].value) / 100
            : 1;
        //apply proportion factor here, since we currently use proportion as portion of work
        const workPortion =
          (utilisationBau
            ? cohort * this.getOperationsBau(tSetup)
            : transitionElement
              ? this._operationsShiftedByTe.get(transitionElement)?.get(tReport) || 0
              : cohort * this._operations) *
          operationsToWorkCoefficient *
          proportion;

        const resourceToWorkConversionCoefficient = getValueFromFactor(
          <EvaluatedFactor>input.resourceToWork,
          variables,
          {
            bau: utilisationBau,
          }
        );
        const amount = workPortion * resourceToWorkConversionCoefficient;
        if (resourceConsumptions[input.resource] === undefined) {
          resourceConsumptions[input.resource] = 0;
        }
        resourceConsumptions[input.resource] += amount;
      });
    });
    return resourceConsumptions;
  }

  getResourceKeyFromProportionParameter(proportionVariable: string): string {
    let resourceKey = undefined;
    this.work.forEach((w) => {
      resourceKey = w.input.find((i) => i.resourceProportion === proportionVariable)?.resource; //NB: Currently we don't support multiple work:s with the same resources, hence this simple solution
    });
    return resourceKey;
  }

  /**
   * Combined `emission factor / kWh` based on consumed resources
   * @param resources
   * @param variables
   * Note that this method can only supply a correct result if all work relations have the same operations_to_work_coefficient
   * Currently we must expect this to be true for all CCCs that are referenced by TTs with weight inversion functions
   */
  getSimpleCccEmissionFactorForWeightInversion(variables: KeyMap<Variable>, bau: boolean = false): number {
    return sumArray(
      this.work.map((work) => {
        return this.getWorkEmissionFactor(work, variables, bau);
      })
    );
  }

  getWork(variables: KeyMap<Variable>): KeyMap<number> {
    return this.work.reduce((acc, work) => {
      const operationsToWorkCoefficient = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables);
      acc[work.work] = this._operations * operationsToWorkCoefficient;
      return acc;
    }, {});
  }

  getWorkEmissionFactor(work: Work, variables: KeyMap<Variable>, utilisationBau: boolean = false): number {
    return sumArray(this._getProportionateResourceEmissionFactors(work, variables, utilisationBau));
  }

  registerCarbonAbatement(transitionElement: TransitionElement, amount: number): void {
    if (transitionElement) {
      this._carbonAbatementPerTE[transitionElement] ??= 0;
      this._carbonAbatementPerTE[transitionElement] += amount ?? 0;
    }
  }

  registerResourceDemand(tSetup: number, tReport: number, variables: KeyMap<Variable>): void {
    const resourceConsumptions: { [resource: string]: number } = this.getResourceConsumption(
      tSetup,
      tReport,
      variables
    );
    Object.keys(resourceConsumptions).forEach((resource) => {
      this._resources[resource].registerDemand(resourceConsumptions[resource]);
    });
  }

  resetCarbonAbatement(): void {
    this._carbonAbatementPerTE = {};
  }

  resetOperations(tSetup: number, tReport?: number): void {
    this._operations = this.getOperationsBau(tSetup);
    const tForShifted = tReport !== undefined ? tReport : tSetup; // we may want to set up the environment for one t and set up reporting for another t
    this._operationsShifted.set(tForShifted, 0);
    this._operationsShiftedByTe.forEach((_, key, __) => this._operationsShiftedByTe.get(key).set(tForShifted, 0));
  }

  private _evaluateCost(
    tSetup: number,
    tReport: number,
    cost: Cost,
    variables: KeyMap<Variable>,
    resources: KeyMap<Resource>,
    stocks: KeyMap<Stock>,
    cohort: number,
    discountRate: number,
    transitionElement?: TransitionElement,
    economicStockImpact?: EconomicStockImpactType
  ): { cost: number; discountedCost: number } {
    const stockInflow = () => {
      /* Currently only supporting to have one stock linked to one cost */
      if (cost.stocks && cost.stocks.length === 1) {
        const stockName = cost.stocks[0].stock;
        const operationsShiftedThisYear = transitionElement
          ? (this._operationsShiftedByTe.get(transitionElement)?.get(tReport) ?? 0) -
            (this._operationsShiftedByTe.get(transitionElement)?.get(tReport - 1) ?? 0)
          : (this._operationsShifted.get(tReport) ?? 0) - (this._operationsShifted.get(tReport - 1) ?? 0);
        const inflow = stocks[stockName].getStockInflow(
          cohort * this._initialOperations,
          operationsShiftedThisYear,
          economicStockImpact
        );
        return transitionElement ? inflow.inflowDelta : inflow.realInflow;
      }
      return 0;
    };
    const stock = () => {
      /* Currently only supporting to have one stock linked to one cost */
      if (cost.stocks && cost.stocks.length === 1) {
        const stockName = cost.stocks[0].stock;
        return stocks[stockName].getStockAmount(
          cohort * this._initialOperations,
          cohort *
            (transitionElement
              ? (this._operationsShiftedByTe.get(transitionElement)?.get(tReport) ?? 0)
              : this._operations),
          economicStockImpact
        );
      }
      return 0;
    };
    const operations = () => {
      if (transitionElement) {
        return this._operationsShiftedByTe.get(transitionElement)?.get(tReport) ?? 0;
      }
      return cohort * this._operations;
    };
    const resourceNativeAmount = () => {
      if (cost.resources && cost.resources.length > 0) {
        // sum total resource native consumptions
        return cost.resources.reduce((totalNativeConsumption, resource) => {
          const energy = this._getResourceEnergy(tSetup, tReport, variables, {
            cohort,
            transitionElement,
            resource: resource.resource,
          });
          return totalNativeConsumption + resources[resource.resource].getNativeConsumption(energy, variables);
        }, 0);
      }
      return 0;
    };
    const energy = () => {
      return this._getResourceEnergy(tSetup, tReport, variables, { cohort, transitionElement });
    };
    const emissions = () => {
      const emissionsInGrams = this.calculateEmissionsFromOperations(
        transitionElement ? (this._operationsShiftedByTe.get(transitionElement)?.get(tReport) ?? 0) : this._operations,
        variables
      );
      return emissionsInGrams;
    };

    const discountRateFactor = Math.pow(1 - discountRate / 100, tReport);
    const costAmount = getValueFromFactor(<EvaluatedFactor>cost.costFactor, variables, {
      bau: false,
      costRelatedCallbacks: { stock, stockInflow, emissions, energy, operations, resourceNativeAmount },
    });
    const discountedCost = costAmount * discountRateFactor;

    return { cost: costAmount, discountedCost };
  }

  private _getResourceEnergy(
    tSetup: number,
    tReport: number,
    variables: KeyMap<Variable>,
    options: { cohort: number; transitionElement?: TransitionElement; resource?: string }
  ) {
    const resourceEnergyConsumption = this.getResourceConsumption(
      tSetup,
      tReport,
      variables,
      undefined,
      options.cohort,
      options.transitionElement
    );
    if (options.resource) {
      return resourceEnergyConsumption[options.resource] || 0;
    }
    let energy = 0;
    Object.keys(resourceEnergyConsumption).forEach((key) => {
      energy += resourceEnergyConsumption[key];
    });
    return energy;
  }

  //returns array of factors that show emissions from the work item's resources, per work unit, after weighting with proportions
  private _getProportionateResourceEmissionFactors(
    work: Work,
    variables: KeyMap<Variable>,
    utilisationBau: boolean = false
  ): number[] {
    return work.input.map((i) => {
      const emissionFactor = this.getEmissionFactorOfInput(i, variables, false, utilisationBau);
      const proportion =
        typeof i.resourceProportion === 'string' && variables[i.resourceProportion] // if string, proportion is reference to proportion-variable
          ? utilisationBau
            ? variables[i.resourceProportion].referenceValue / 100
            : variables[i.resourceProportion].value / 100
          : typeof i.resourceProportion === 'number'
            ? i.resourceProportion
            : 1;
      const workToResourceConversionCoefficient = getValueFromFactor(<EvaluatedFactor>i.resourceToWork, variables, {
        bau: utilisationBau,
      });

      return workToResourceConversionCoefficient * emissionFactor * proportion;
    });
  }

  private _init(variables: KeyMap<Variable>): void {
    this._initialOperations = variables[this._initialOperationRef]?.value;
    this._initialPopulation = variables['population'].value;
    this._operationGrowthFactorPerCapita = this._operationGrowthFactorPerCapita;
    this._populationGrowthRate = variables['population_growth_rate']?.value / 100; // percentage
    this.work.forEach((work) => {
      this._initialWorkRelatedVariableValues['energy_intensity'] = getValueFromFactor(
        <EvaluatedFactor>work.operationToWork,
        variables
      );
      work.input = work.input.map((input) => {
        if (typeof input.resourceProportion === 'string') {
          this._initialWorkRelatedVariableValues[input.resourceProportion] = variables[input.resourceProportion].value;
        }
        input.resourceToWork = instantiateFactor(input.resourceToWork);
        input.emissionFactor = instantiateFactor(input.emissionFactor);
        return input;
      });
      work.output = work.output?.map((output) => {
        if (output.workToResource) {
          output.workToResource = instantiateFactor(output.workToResource);
        }
        return output;
      });
    });
  }

  private _instantiateCosts(costs: Cost[]): Cost[] {
    return costs.map((cost) => ({
      ...cost,
      costFactor: instantiateFactor(cost.costFactor),
    }));
  }

  private _instantiateWork(work: Work[]): Work[] {
    return work.map((w) => {
      let output = w.output;
      if (w.output?.length) {
        output = w.output.map((output) => {
          return {
            ...output,
            workToResource: instantiateFactor(output.workToResource),
          };
        });
      }
      return {
        ...w,
        operationToWork: instantiateFactor(w.operationToWork),
        output,
      };
    });
  }

  private _populationAtYear(t): number {
    return Math.pow(1 + this._populationGrowthRate, t) * this._initialPopulation;
  }

  private _produceResource(t: number, variables: KeyMap<Variable>, work: Work, input: Input, output: Output): void {
    const operationToWork = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables);
    const energyAmount = this._operations * operationToWork;
    let workToResource = getValueFromFactor(<EvaluatedFactor>output.workToResource, variables);
    if (input.resourceProportion) {
      workToResource *= getValueFromVariableName(input.resourceProportion, variables, 0, { bau: false });
    }
    let producedAmount = energyAmount * workToResource;
    const originResource = input.resource;
    let emissionFactor = this.getEmissionFactorOfInput(input, variables, false);
    emissionFactor *= operationToWork;
    emissionFactor *= getValueFromFactor(<EvaluatedFactor>input.resourceToWork, variables);
    /**
     * Following GHG protocol, efficiency method has been chosen with current CHP energy efficiency variables
     * >>>> In the future a theoretical energy efficiency for each output could be used, benefitting the primary output emission factor
     * https://ghgprotocol.org/sites/default/files/CHP_guidance_v1.0.pdf (Equation on bottom of page 9 and cont.)
     *
     * this will, with the use of actual energy efficiencies always result in {1 / $amount_of_outputs}, i.e. 0.5 in the case of CHP
     */
    if (work.output.length > 1 && producedAmount > 0) {
      const totalEmissions =
        energyAmount *
        getValueFromFactor(<EvaluatedFactor>input.resourceToWork, variables) *
        getValueFromFactor(<EvaluatedFactor>input.emissionFactor, variables);
      let efficiencyMethodDenominator = 0;
      work.output.forEach((o) => {
        const outputSpecificWorkToResource = getValueFromFactor(<EvaluatedFactor>o.workToResource, variables); // will eventually be exchanged to theoreticalEfficiency (mentioned in comment above)
        if (outputSpecificWorkToResource > 0) {
          const outputProduced = energyAmount * outputSpecificWorkToResource;
          efficiencyMethodDenominator += outputProduced / outputSpecificWorkToResource;
        }
      });
      const efficiencyMethodNominator = producedAmount / workToResource; // workToResource will eventually be exchanged to theoreticalEfficiency (mentioned in comment above)
      const proportionOfEmissionAllocatedToOutputProduced = efficiencyMethodNominator / efficiencyMethodDenominator;
      emissionFactor = (proportionOfEmissionAllocatedToOutputProduced * totalEmissions) / producedAmount;
    }

    const consideredPrimaryProduction = work.output.length === 1 || output.primary;
    const primaryResourceProduced = work.output.find((o) => o.primary) ?? output;
    this._resources[output.resource].produceResource(
      t,
      producedAmount,
      originResource,
      this.id,
      emissionFactor,
      primaryResourceProduced?.resource,
      consideredPrimaryProduction
    );
  }
}
